// The value side of one condition on a policy rule.
//
// Renders a picker of what already exists — departments, designations, roles,
// employees — rather than a text box, because several of these predicates are
// matched by ID (see policyPredicateOptions.ts) and nobody can type a UUID.
// Predicates with no known vocabulary (a salary, a number of years) keep the
// plain input.

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  hasOptions,
  loadPredicateOptions,
  type PredicateOption,
} from './policyPredicateOptions';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { EmployeeMultiSelect } from '../../components/ui/EmployeeMultiSelect';

interface Props {
  predicateKey: string;
  /** Catalog type, e.g. "list[str]" | "str" | "number". */
  type: string;
  value: unknown;
  onChange: (next: unknown) => void;
}

const LIST_TYPES = new Set(['list[str]', 'list[int]']);
const INPUT = 'border border-[#e8e3d8] rounded-lg px-2 py-1 text-sm w-full bg-white';

const asList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

export default function PolicyPredicateValue({
  predicateKey, type, value, onChange,
}: Props) {
  const isList = LIST_TYPES.has(type);
  const [options, setOptions] = useState<PredicateOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!hasOptions(predicateKey)) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    loadPredicateOptions(predicateKey)
      .then((opts) => { if (!cancelled) setOptions(opts); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [predicateKey]);

  const selected = useMemo(
    () => (isList ? asList(value) : [String(value ?? '')].filter(Boolean)),
    [isList, value],
  );

  // A value that no longer matches anything — a deleted department, say — must
  // still be shown and stay selected. Dropping it silently would quietly widen
  // the rule's audience the next time someone saved it.
  const shown = useMemo(() => {
    if (!options) return [];
    const known = new Set(options.map((o) => o.value));
    const orphans = selected
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, label: `${v} — no longer exists` }));
    return [...options, ...orphans];
  }, [options, selected]);

  // Free-form predicate, or the lookup failed — fall back to typing.
  if (!hasOptions(predicateKey) || failed) {
    return (
      <div className="flex-1 min-w-0">
        <input
          className={INPUT}
          value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
          placeholder={isList ? 'separate with commas' : ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (!isList) return onChange(type === 'number' && raw !== '' ? Number(raw) : raw);
            onChange(
              raw.split(',').map((s) => s.trim()).filter(Boolean)
                .map((s) => (type === 'list[int]' ? Number(s) : s)),
            );
          }}
        />
        {failed && (
          <p className="mt-1 text-[11px] text-amber-700 flex items-center gap-1">
            <AlertTriangle size={11} />
            Couldn't load the list — type the values instead.
          </p>
        )}
      </div>
    );
  }

  if (loading || !options) {
    return (
      <div className="flex-1 min-w-0">
        <div className={`${INPUT} text-gray-400`}>Loading choices…</div>
      </div>
    );
  }

  // Single-value condition (gender) — a plain dropdown says it all.
  if (!isList) {
    return (
      <div className="flex-1 min-w-0">
        <Select value={String(value ?? '')} onValueChange={onChange}>
          <SelectTrigger aria-label={`${predicateKey} value`} className="h-9">
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {shown.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // The app's standard checkbox picker: a trigger that reports the count, and
  // a popup with search, Select All and Done. It replaced an always-open
  // checkbox panel that had no way to close and pushed the rest of the form
  // down the dialog.
  return (
    <div className="flex-1 min-w-0">
      <EmployeeMultiSelect
        values={selected}
        onChange={(next) => onChange(next)}
        options={shown.map((o) => ({ id: o.value, name: o.label }))}
        placeholder="Choose…"
        searchPlaceholder="Search…"
        emptyText="Nothing set up yet."
        countLabel={(n) => `${n} chosen`}
        noMatchText={(q) => `Nothing matches “${q}”.`}
      />
      {selected.length === 0 && (
        <p className="mt-1 text-[11px] text-gray-400">
          Nothing chosen — this condition matches nobody until you pick at least one.
        </p>
      )}
    </div>
  );
}
